import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedAxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { sortStrings } from "@utils/string-util";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { RelyingPartyTableCell } from "./relying-party-table-cell/relying-party-table-cell";
import styles from "./relying-parties-table-container.less";
import { EntityStatusTypes } from "@enums/entity-status-types";
import Badge from "react-bootstrap/Badge";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";

type RelyingPartiesTableContainerProps = {
    canViewCustomHostNames: boolean;
    canRestoreDueToLimits: boolean;
    relyingParties: Array<FormattedAxnRelyingParty>;
    allowedRelyingParties: Array<FormattedAxnRelyingParty>;
    tableState: TableState;
    isPageHeight: boolean;
};

export type RelyingPartiesTableDataRow = FormattedAxnRelyingParty & {
    companyName: string;
};

export const RelyingPartiesTableContainer = (props: RelyingPartiesTableContainerProps) => {
    const {
        canViewCustomHostNames,
        canRestoreDueToLimits,
        relyingParties,
        allowedRelyingParties,
        tableState,
        isPageHeight
    } = props;
    const dispatch = useDispatch();

    const relyingPartyTableData: Array<RelyingPartiesTableDataRow> = useMemo(() => {
        const allowedRpIds = allowedRelyingParties.map(rp => rp.id);
        return relyingParties.map(relyingParty => {
            const canShowView = allowedRpIds.includes(relyingParty.id);
            return {
                ...relyingParty,
                companyName: relyingParty.company?.name || resources.common.notFound,
                customAXNHostName: canShowView ?
                    relyingParty.customAXNHostName || resources.common.notFound
                    : resources.common.notFound,
                whiteLabelAXNUI3HostName: canShowView ?
                    relyingParty.whiteLabelAXNUI3HostName || resources.common.notFound
                    : resources.common.notFound,
                whiteLabelKBA2HostName: canShowView ?
                    relyingParty.whiteLabelKBA2HostName || resources.common.notFound
                    : resources.common.notFound,
                whiteLabelDCUIHostName: canShowView ?
                    relyingParty.whiteLabelDCUIHostName || resources.common.notFound
                    : resources.common.notFound,
                contractCode: relyingParty.contractCode || resources.common.notFound
            };
        });
    }, [relyingParties]);

    const columnHelper = createColumnHelper<RelyingPartiesTableDataRow>();

    const columns = useMemo(() => {
        const finalColumns = [];

        finalColumns.push(
            columnHelper.accessor("name", {
                header: resources.tables.relyingParties.columns.name.header,
                filterFn: "fuzzyText",
                cell: (props) => (
                    <RelyingPartyTableCell
                        data={props.row.original}
                        canRestoreDueToLimits={canRestoreDueToLimits}
                    />
                ),
                sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
            })
        );

        if (canViewCustomHostNames) {
            finalColumns.push(
                columnHelper.accessor("companyName", {
                    header: resources.tables.relyingParties.columns.organization.header,
                    filterFn: "fuzzyText",
                    sortingFn: (a, b) => sortStrings(a.original.companyName, b.original.companyName)
                }),
                columnHelper.accessor("customAXNHostName", {
                    header: resources.tables.relyingParties.columns.axn.header,
                    filterFn: "fuzzyText"
                }),
                columnHelper.accessor("whiteLabelAXNUI3HostName", {
                    header: resources.tables.relyingParties.columns.axnui.header,
                    filterFn: "fuzzyText"
                }),
                columnHelper.accessor("whiteLabelKBA2HostName", {
                    header: resources.tables.relyingParties.columns.kbaui.header,
                    filterFn: "fuzzyText"
                }),
                columnHelper.accessor("whiteLabelDCUIHostName", {
                    header: resources.tables.relyingParties.columns.dcui.header,
                    filterFn: "fuzzyText"
                }),
                columnHelper.accessor("contractCode", {
                    header: resources.tables.relyingParties.columns.blockBuyCode.header,
                    filterFn: "fuzzyText",
                    sortingFn: (a, b) => sortStrings(a.original.contractCode, b.original.contractCode)
                }),
            );
        }

        finalColumns.push(
            columnHelper.accessor("status", {
                header: resources.tables.relyingParties.columns.status.header,
                filterFn: "singleSelectFilter",
                meta: {
                    filterOptions: ENTITY_STATUS_OPTIONS
                },
                cell: (props) => {
                    switch (props.getValue()) {
                        case EntityStatusTypes.ACTIVE:
                            return <Badge bg={"success"}>{resources.common.active}</Badge>;
                        case EntityStatusTypes.INACTIVE:
                            return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                        default:
                            return <span>{resources.common.notFound}</span>;
                    }
                }
            })
        );

        return finalColumns;

    }, [canViewCustomHostNames, relyingPartyTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: relyingPartyTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.RELYING_PARTIES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.RELYING_PARTIES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.RELYING_PARTIES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="relying-parties"
                useTableResult={useTableResult}
                className={canViewCustomHostNames ? styles.relyingPartiesTable : styles.relyingPartiesSmallTable}
                headerColumnKeyPrefix={"relying-parties"}
            />
            <PaginationContainer
                idPrefix="relying-parties"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};